import React, { useCallback, useEffect, useState, useContext } from 'react';
import { Loader2, AlertCircle, X, Edit2, Plus, Trash2, Check, RefreshCw } from 'lucide-react';
import { semanticBrandingService, Competitor } from '../../services/semanticBranding';
import CompetitorEditModal from './CompetitorEditModal';
import { ToastContext } from '../../App';
interface CompetitorsStepProps {
  documentId: string;
  existingSuggestions?: Competitor[];
  existingSelections?: Competitor[];
  onSave: (competitors: Competitor[]) => void;
  onNextStep: () => void;
  onPreviousStep: () => void;
  autoFetchSuggestions?: boolean;
}
const CompetitorsStep: React.FC<CompetitorsStepProps> = ({
  documentId,
  existingSuggestions = [],
  existingSelections = [],
  onSave,
  onNextStep,
  onPreviousStep,
  autoFetchSuggestions = false
}) => {
  const {
    addToast
  } = useContext(ToastContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [suggestedCompetitors, setSuggestedCompetitors] = useState<Competitor[]>(existingSuggestions);
  const [filteredSuggestions, setFilteredSuggestions] = useState<Competitor[]>([]);
  const [selectedCompetitors, setSelectedCompetitors] = useState<Competitor[]>(existingSelections);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCompetitor, setCurrentCompetitor] = useState<Competitor | null>(null);
  const [isNewCompetitor, setIsNewCompetitor] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  // Filter suggestions to remove already selected competitors
  useEffect(() => {
    const filtered = suggestedCompetitors.filter(suggestion => !selectedCompetitors.some(selected => selected.marca === suggestion.marca));
    setFilteredSuggestions(filtered);
  }, [suggestedCompetitors, selectedCompetitors]);
  // Track changes to selected competitors
  useEffect(() => {
    if (JSON.stringify(selectedCompetitors) !== JSON.stringify(existingSelections)) {
      setHasUnsavedChanges(true);
    }
  }, [selectedCompetitors, existingSelections]);
  // Warn user before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = e => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'Você tem alterações não salvas. Tem certeza que deseja sair?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);
  // Fetch competitor suggestions if none exist or autoFetchSuggestions is true
  useEffect(() => {
    // Auto-fetch suggestions if explicitly requested via prop
    if (autoFetchSuggestions) {
      setIsLoadingSuggestions(true);
      fetchCompetitorSuggestions();
      return;
    }
    // Or if there are no existing suggestions and no selected competitors
    if (existingSuggestions.length === 0 && existingSelections.length === 0) {
      setIsLoadingSuggestions(true);
      fetchCompetitorSuggestions();
    }
  }, [existingSuggestions.length, existingSelections.length, autoFetchSuggestions]);
  const fetchCompetitorSuggestions = async () => {
    setIsLoadingSuggestions(true);
    setError('');
    try {
      const response = await semanticBrandingService.suggestCompetitors(documentId);
      // Log para diagnóstico - verificar os dados recebidos da API
      console.log('Dados de concorrentes recebidos da API:', response.concorrentes);
      // Verificar se todos os graus de concorrência são 0
      const todosZerados = response.concorrentes.every(comp => !comp.grau_de_concorrencia || comp.grau_de_concorrencia === 0);
      let concorrentes = response.concorrentes || [];
      // Se todos os valores forem 0 ou undefined, atribuir valores temporários
      if (todosZerados && concorrentes.length > 0) {
        console.warn('Todos os graus de concorrência estão zerados! Aplicando valores temporários.');
        // Atribuir valores decrescentes baseados na posição (solução temporária)
        concorrentes = concorrentes.map((comp, index) => ({
          ...comp,
          grau_de_concorrencia: Math.max(90 - index * 10, 10) // 90%, 80%, 70%... no mínimo 10%
        }));
      }
      setSuggestedCompetitors(concorrentes);
    } catch (error) {
      console.error('Failed to fetch competitor suggestions:', error);
      setError(error.response?.data?.message || 'Erro ao buscar sugestões de concorrentes.');
    } finally {
      setIsLoadingSuggestions(false);
    }
  };
  const handleToggleCompetitor = (competitor: Competitor) => {
    // Check if competitor is already selected
    const isAlreadySelected = selectedCompetitors.some(c => c.marca === competitor.marca);
    if (isAlreadySelected) {
      // Remove from selected
      setSelectedCompetitors(selectedCompetitors.filter(c => c.marca !== competitor.marca));
    } else {
      // Add to selected if under limit
      if (selectedCompetitors.length >= 10) {
        setError('Limite máximo de 10 concorrentes atingido.');
        return;
      }
      const newCompetitor = {
        ...competitor
      };
      delete newCompetitor.grau_de_concorrencia; // Remove grade if present
      setSelectedCompetitors([...selectedCompetitors, newCompetitor]);
    }
  };
  const handleRemoveCompetitor = (index: number) => {
    const newSelectedCompetitors = [...selectedCompetitors];
    newSelectedCompetitors.splice(index, 1);
    setSelectedCompetitors(newSelectedCompetitors);
  };
  const handleEditCompetitor = (index: number) => {
    // Make a deep copy of the competitor to avoid modifying the original directly
    const competitorToEdit = JSON.parse(JSON.stringify(selectedCompetitors[index]));
    setCurrentCompetitor(competitorToEdit);
    setEditingIndex(index);
    setIsNewCompetitor(false);
    setIsModalOpen(true);
  };
  const handleAddNew = () => {
    if (selectedCompetitors.length >= 10) {
      setError('Limite máximo de 10 concorrentes atingido.');
      return;
    }
    setCurrentCompetitor({
      marca: '',
      url: '',
      descricao: ''
    });
    setIsNewCompetitor(true);
    setIsModalOpen(true);
  };
  const handleSaveCompetitor = (competitor: Competitor) => {
    // Garantir que o concorrente tenha uma descrição
    const competitorWithDesc = {
      ...competitor,
      descricao: competitor.descricao?.trim() ? competitor.descricao.trim() : `Concorrente da marca: ${competitor.marca}`
    };
    if (isNewCompetitor) {
      // Add new competitor
      const updatedCompetitors = [...selectedCompetitors, competitorWithDesc];
      setSelectedCompetitors(updatedCompetitors);
      // Save the updated list to the database immediately
      saveCompetitorsToDatabase(updatedCompetitors);
      // Adicionado toast apenas aqui para evitar duplicação
      addToast('success', 'Concorrente adicionado com sucesso!');
    } else if (editingIndex !== null) {
      // Update existing competitor
      const updatedCompetitors = [...selectedCompetitors];
      updatedCompetitors[editingIndex] = competitorWithDesc;
      setSelectedCompetitors(updatedCompetitors);
      // Save the updated list to the database immediately
      saveCompetitorsToDatabase(updatedCompetitors);
      // Adicionado toast apenas aqui para evitar duplicação
      addToast('success', 'Concorrente atualizado com sucesso!');
    }
    // Close modal and reset state
    setIsModalOpen(false);
    setCurrentCompetitor(null);
    setEditingIndex(null);
    setIsNewCompetitor(false);
  };
  // Helper function to save competitors to the database
  const saveCompetitorsToDatabase = async (competitors: Competitor[]) => {
    setIsLoading(true);
    setError('');
    try {
      // Limpar os dados antes de enviar
      const cleanedCompetitors = competitors.map(comp => ({
        marca: comp.marca.trim(),
        url: comp.url.trim(),
        descricao: comp.descricao?.trim() ? comp.descricao.trim() : `Concorrente da marca: ${comp.marca}`
      }));
      // Save all competitors to the API
      await semanticBrandingService.saveCompetitors(documentId, cleanedCompetitors);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to save competitors:', error);
      setError(error.response?.data?.message || 'Erro ao salvar concorrentes.');
    } finally {
      setIsLoading(false);
    }
  };
  const handleSaveCompetitors = async () => {
    if (selectedCompetitors.length === 0) {
      setError('Selecione pelo menos um concorrente para continuar.');
      return;
    }
    // Preparar os dados para envio à API - garantir que todos os campos necessários estejam presentes
    // e remover campos que a API não espera
    const cleanedCompetitors = selectedCompetitors.map(comp => {
      // Garantir que todos os concorrentes tenham uma descrição válida
      const descricao = comp.descricao?.trim() ? comp.descricao.trim() : `Concorrente da marca: ${comp.marca}`;
      // Retornar apenas os campos que a API espera
      return {
        marca: comp.marca.trim(),
        url: comp.url.trim(),
        descricao: descricao
      };
    });
    // Validar cada concorrente antes de enviar
    const invalidCompetitors = cleanedCompetitors.filter(comp => {
      // Verificar se tem marca
      if (!comp.marca || comp.marca === '') {
        return true;
      }
      // Verificar se tem URL
      if (!comp.url || comp.url === '') {
        return true;
      }
      // Verificar se tem descrição
      if (!comp.descricao || comp.descricao === '') {
        return true;
      }
      // Verificar se a URL está em formato válido
      try {
        new URL(comp.url);
        return false;
      } catch (e) {
        return true;
      }
    });
    if (invalidCompetitors.length > 0) {
      setError(`Há ${invalidCompetitors.length} concorrente(s) com dados inválidos. Verifique se todos possuem nome, URL em formato válido e descrição.`);
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      console.log('Enviando concorrentes para a API:', cleanedCompetitors);
      // Save all competitors to the API
      await semanticBrandingService.saveCompetitors(documentId, cleanedCompetitors);
      setHasUnsavedChanges(false);
      onSave(cleanedCompetitors);
    } catch (error) {
      console.error('Failed to save competitors:', error);
      // Melhorando a mensagem de erro para mostrar mais detalhes
      if (error.response?.data?.message) {
        setError(`Erro ao salvar concorrentes: ${error.response.data.message}`);
      } else if (error.response?.status === 400) {
        setError('Dados inválidos. Verifique se todos os concorrentes possuem nome, URL válida e descrição.');
      } else if (error.response?.status === 401) {
        setError('Sessão expirada. Por favor, faça login novamente.');
      } else {
        setError('Erro ao salvar concorrentes. Tente novamente mais tarde.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handleDeleteAll = () => {
    if (window.confirm('Tem certeza que deseja remover todos os concorrentes?')) {
      // Only update local state, don't call API until save
      setSelectedCompetitors([]);
    }
  };
  // Remove a suggestion from the list
  const handleRemoveSuggestion = (competitorToRemove: Competitor) => {
    setSuggestedCompetitors(suggestedCompetitors.filter(competitor => competitor.marca !== competitorToRemove.marca));
  };
  // Get favicon from URL
  const getFavicon = (url: string) => {
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
    } catch (e) {
      return `https://www.google.com/s2/favicons?domain=example.com&sz=128`;
    }
  };
  // Check if a competitor is selected
  const isCompetitorSelected = (competitor: Competitor) => {
    return selectedCompetitors.some(c => c.marca === competitor.marca);
  };
  return <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100" data-id="element-986">
      <h2 className="text-xl font-semibold mb-6 text-gray-900" data-id="element-987">Concorrentes</h2>
      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md flex items-start" data-id="element-988">
          <AlertCircle size={18} className="mr-2 mt-0.5 flex-shrink-0" data-id="element-989" />
          <p data-id="element-990">{error}</p>
        </div>}

      {/* Competitor edit modal */}
      <CompetitorEditModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} competitor={currentCompetitor} onSave={handleSaveCompetitor} isNew={isNewCompetitor} data-id="element-991" />

      {/* Selected competitors */}
      <div className="mb-8" data-id="element-992">
        <div className="flex justify-between items-center mb-4" data-id="element-993">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-994">
            Concorrentes selecionados
          </h3>
          <div className="flex space-x-2" data-id="element-995">
            <button onClick={handleAddNew} disabled={selectedCompetitors.length >= 10} className={`px-3 py-1.5 rounded-md flex items-center text-sm ${selectedCompetitors.length >= 10 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-black text-white hover:bg-gray-800'}`} data-id="element-996">
              <Plus size={14} className="mr-1" data-id="element-997" />
              Adicionar
            </button>
            {selectedCompetitors.length > 0 && <button onClick={handleDeleteAll} className="px-3 py-1.5 bg-red-50 text-red-600 rounded-md hover:bg-red-100 flex items-center text-sm" data-id="element-998">
                <Trash2 size={14} className="mr-1" data-id="element-999" />
                Remover todos
              </button>}
          </div>
        </div>
        {selectedCompetitors.length === 0 ? <div className="p-4 bg-gray-50 rounded-md text-gray-700 text-center" data-id="element-1000">
            Nenhum concorrente selecionado
          </div> : <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3" data-id="element-1001">
            {selectedCompetitors.map((competitor, index) => <div key={index} className="p-3 border border-gray-200 rounded-md relative hover:border-[#1649ff] hover:bg-blue-50 transition-colors" data-id="element-1002">
                <div className="absolute top-2 right-2 flex space-x-1" data-id="element-1003">
                  <button onClick={() => handleEditCompetitor(index)} className="p-1 text-gray-400 hover:text-[#1649ff] rounded-full" data-id="element-1004">
                    <Edit2 size={14} data-id="element-1005" />
                  </button>
                  <button onClick={() => handleRemoveCompetitor(index)} className="p-1 text-gray-400 hover:text-red-500 rounded-full" data-id="element-1006">
                    <X size={14} data-id="element-1007" />
                  </button>
                </div>
                <div className="flex items-center mb-2" data-id="element-1008">
                  <img src={getFavicon(competitor.url)} alt={`${competitor.marca} favicon`} className="w-5 h-5 mr-2" onError={e => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = 'https://www.google.com/s2/favicons?domain=example.com&sz=128';
            }} data-id="element-1009" />
                  <h4 className="font-medium text-sm truncate text-gray-900" data-id="element-1010">
                    {competitor.marca}
                  </h4>
                </div>
                <p className="text-xs text-blue-600 truncate" data-id="element-1011">
                  {competitor.url}
                </p>
              </div>)}
          </div>}
      </div>

      {/* Suggested competitors */}
      <div data-id="element-1012">
        <div className="flex justify-between items-center mb-4" data-id="element-1013">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-1014">
            Sugestões de concorrentes
          </h3>
          {!isLoadingSuggestions && suggestedCompetitors.length > 0 && <button onClick={fetchCompetitorSuggestions} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center text-sm" disabled={isLoadingSuggestions} data-id="element-1015">
              <RefreshCw size={14} className={`mr-1 ${isLoadingSuggestions ? 'animate-spin' : ''}`} data-id="element-1016" />
              Sugerir novos
            </button>}
        </div>
        {isLoadingSuggestions ? <div className="flex justify-center items-center py-8" data-id="element-1017">
            <Loader2 size={24} className="animate-spin text-[#1649ff] mr-2" data-id="element-1018" />
            <span className="text-gray-900" data-id="element-1019">
              Gerando sugestões de concorrentes...
            </span>
          </div> : filteredSuggestions.length === 0 ? <div className="p-4 bg-gray-50 rounded-md text-gray-700 text-center" data-id="element-1020">
            Nenhuma sugestão disponível
            <button onClick={fetchCompetitorSuggestions} className="ml-2 text-[#1649ff] hover:underline" data-id="element-1021">
              Gerar sugestões
            </button>
          </div> : <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3" data-id="element-1022">
            {filteredSuggestions.sort((a, b) => (b.grau_de_concorrencia || 0) - (a.grau_de_concorrencia || 0)).slice(0, 9) // Limit to 9 suggestions (3 rows of 3)
        .map((competitor, index) => {
          const competitionDegree = competitor.grau_de_concorrencia || 0;
          return <div key={index} className="p-3 border border-gray-200 rounded-md cursor-pointer relative transition-colors hover:border-[#1649ff] hover:bg-blue-50 group" onClick={() => handleToggleCompetitor(competitor)} data-id="element-1023">
                    <button onClick={e => {
              e.stopPropagation(); // Prevent card click from triggering
              handleRemoveSuggestion(competitor);
            }} className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1024">
                      <X size={14} data-id="element-1025" />
                    </button>
                    <div className="flex items-center mb-2" data-id="element-1026">
                      <img src={getFavicon(competitor.url)} alt={`${competitor.marca} favicon`} className="w-5 h-5 mr-2" onError={e => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = 'https://www.google.com/s2/favicons?domain=example.com&sz=128';
              }} data-id="element-1027" />
                      <h4 className="font-medium text-sm truncate text-gray-900" data-id="element-1028">
                        {competitor.marca}
                      </h4>
                    </div>
                    <p className="text-xs text-blue-600 truncate mb-2" data-id="element-1029">
                      {competitor.url}
                    </p>
                    {/* Competition degree indicator */}
                    <div className="mt-2" data-id="element-1030">
                      <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden" data-id="element-1031">
                        <div className="bg-blue-500 h-1.5 rounded-full" style={{
                  width: `${competitionDegree}%`
                }} data-id="element-1032"></div>
                      </div>
                      <div className="flex justify-between mt-1" data-id="element-1033">
                        <span className="text-xs text-gray-700" data-id="element-1034">
                          Relevância
                        </span>
                        <span className="text-xs text-gray-900 font-medium" data-id="element-1035">
                          {Math.round(competitionDegree)}%
                        </span>
                      </div>
                    </div>
                  </div>;
        })}
          </div>}
      </div>

      {/* Action buttons */}
      <div className="mt-8 flex justify-between" data-id="element-1036">
        <button onClick={onPreviousStep} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200" disabled={isLoading} data-id="element-1037">
          Voltar
        </button>
        <button onClick={handleSaveCompetitors} className="px-4 py-2 bg-[#1649ff] text-white rounded-md hover:bg-blue-700 flex items-center" disabled={isLoading || selectedCompetitors.length === 0} data-id="element-1038">
          {isLoading ? <>
              <Loader2 size={16} className="mr-2 animate-spin" data-id="element-1039" />
              Salvando...
            </> : 'Continuar para Atributos'}
        </button>
      </div>

      {hasUnsavedChanges && <div className="mt-4 p-3 bg-yellow-50 text-yellow-700 rounded-md flex items-start" data-id="element-1040">
          <AlertCircle size={18} className="mr-2 mt-0.5 flex-shrink-0" data-id="element-1041" />
          <p data-id="element-1042">
            Você tem alterações não salvas. Clique em "Continuar para Atributos"
            para salvá-las.
          </p>
        </div>}
    </div>;
};
export default CompetitorsStep;